import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Globe } from 'lucide-react';

export default function LanguageSelector({ variant = 'default' }) {
    const { currentLanguage, changeLanguage, languages } = useLanguage();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const DropdownMenu = () => (
        <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden z-50">
            {languages.map((lang) => (
                <button
                    key={lang.code}
                    onClick={() => { changeLanguage(lang.code); setOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left text-[13px] font-bold transition-all hover:bg-blue-50 ${
                        currentLanguage.code === lang.code
                            ? 'text-blue-600 bg-blue-50'
                            : 'text-gray-700'
                    }`}
                >
                    <span className="text-[18px] leading-none">{lang.flag}</span>
                    <div>
                        <p className="text-[13px] font-bold leading-none mb-0.5">{lang.nativeLabel}</p>
                        <p className="text-[10px] font-medium text-gray-400 leading-none">{lang.label}</p>
                    </div>
                    {currentLanguage.code === lang.code && (
                        <div className="ml-auto w-2 h-2 rounded-full bg-blue-500" />
                    )}
                </button>
            ))}
        </div>
    );

    // Compact variant — for use inside app Header bar
    if (variant === 'compact') {
        return (
            <div ref={ref} className="relative">
                <button
                    onClick={() => setOpen(!open)}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-100 rounded-xl text-[13px] font-bold text-gray-700 shadow-sm hover:bg-gray-50 transition-all group"
                    title="Change Language"
                >
                    <Globe size={15} className="text-blue-500 group-hover:scale-110 transition-transform" />
                    <span className="hidden sm:inline">{currentLanguage.flag} {currentLanguage.nativeLabel}</span>
                    <span className="sm:hidden">{currentLanguage.flag}</span>
                </button>
                {open && <DropdownMenu />}
            </div>
        );
    }

    // Landing page variant — for navbar on landing page
    if (variant === 'landing') {
        return (
            <div ref={ref} className="relative">
                <button
                    onClick={() => setOpen(!open)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[13px] font-bold text-[#111827] hover:bg-gray-50 transition-all shadow-sm"
                    title="Change Language"
                >
                    <Globe size={14} className="text-blue-500" />
                    <span className="hidden sm:inline">{currentLanguage.flag} {currentLanguage.nativeLabel}</span>
                    <span className="sm:hidden">{currentLanguage.flag}</span>
                </button>
                {open && <DropdownMenu />}
            </div>
        );
    }

    // Default fallback
    return null;
}
